import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Divider,
  FormControlLabel, Radio, RadioGroup, Stack, TextField, Typography,
} from '@mui/material';
import { BACKEND_URL } from '../utils/apiConfig';
import { authFetch } from '../utils/authHelpers';
import { leesJson } from '../utils/apiFout';
import { useUserRole } from '../contexts/UserRoleContext';
import {
  DEFAULT_CAPACITY_RULES, CONFIGURABLE_TYPES, EXCLUDABLE_STATUSES, VERDELINGEN,
  describeCapacityRules, normalizeCapacityRules,
} from '../utils/capacityCalc';

const T = { font: '"Manrope", system-ui, sans-serif', text: '#2d3335', muted: '#64748b' };

/**
 * "Zo rekenen we de bezetting uit" — de rekenregels van Planner To-Do en het
 * Capaciteit-overzicht, leesbaar én aanpasbaar.
 *
 * Iedereen die de planning mag zien kan de uitleg lezen (de percentages moeten
 * navolgbaar zijn); aanpassen mag wie het recht `planner_todo` (bewerken) heeft —
 * bij ons de planner. De uitleg-lijst wordt uit de ingevulde waarden gegenereerd,
 * dus hij kan niet uit de pas lopen met wat de berekening doet.
 */
export default function CapaciteitsRegels({ onSaved, compact = false }) {
  const { permissions } = useUserRole();
  const canEdit = Boolean(permissions?.planner_todo?.edit);

  const [rules, setRules] = useState(DEFAULT_CAPACITY_RULES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [melding, setMelding] = useState(null);

  useEffect(() => {
    let actief = true;
    authFetch(`${BACKEND_URL}/Api/planning-rules`)
      .then((r) => leesJson(r, 'Capaciteitsregels laden'))
      .then((d) => { if (actief) setRules(normalizeCapacityRules(d.rules)); })
      .catch((e) => {
        if (!actief) return;
        setRules(DEFAULT_CAPACITY_RULES);
        setMelding({ type: 'error', tekst: `${e.message} — je ziet nu de standaardwaarden, niet wat er is opgeslagen.` });
      })
      .finally(() => { if (actief) setLoading(false); });
    return () => { actief = false; };
  }, []);

  const zet = (patch) => { setRules((r) => ({ ...r, ...patch })); setMelding(null); };
  const zetType = (type, waarde) =>
    setRules((r) => ({ ...r, hoursByType: { ...r.hoursByType, [type]: waarde } }));

  const toggleStatus = (status) => setRules((r) => ({
    ...r,
    excludedStatuses: r.excludedStatuses.includes(status)
      ? r.excludedStatuses.filter((s) => s !== status)
      : [...r.excludedStatuses, status],
  }));

  const opslaan = async () => {
    setSaving(true);
    setMelding(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/planning-rules`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rules: normalizeCapacityRules(rules) }),
      });
      if (!r.ok) throw new Error(r.status === 403 ? 'Je mag deze regels niet aanpassen.' : `HTTP ${r.status}`);
      setMelding({ type: 'success', tekst: 'Opgeslagen — de planning rekent hier direct mee.' });
      onSaved?.();
    } catch (e) {
      setMelding({ type: 'error', tekst: `Opslaan mislukt: ${e.message}` });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 2 }}><CircularProgress size={20} /></Box>;

  const uitleg = describeCapacityRules(rules);

  return (
    <Box>
      <Box sx={{ p: 2, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
        <Typography variant="caption" sx={{ fontWeight: 800, color: '#475569', display: 'block', mb: 1, fontFamily: T.font }}>
          Zo rekenen we de bezetting uit
        </Typography>
        <Stack spacing={0.75}>
          {uitleg.map((regel, i) => (
            <Typography key={i} variant="caption" sx={{ color: T.muted, display: 'flex', gap: 0.75, lineHeight: 1.5 }}>
              <span>•</span><span>{regel}</span>
            </Typography>
          ))}
        </Stack>
      </Box>

      {!canEdit && !compact && (
        <Typography variant="caption" sx={{ color: T.muted, display: 'block', mt: 1.5 }}>
          Kloppen deze regels niet? De planner kan ze aanpassen.
        </Typography>
      )}

      {canEdit && (
        <Box sx={{ mt: 2.5 }}>
          <Typography sx={{ fontFamily: T.font, fontWeight: 700, fontSize: 14, color: T.text, mb: 1.5 }}>
            Regels aanpassen
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              type="number" size="small" label="Uren per werkdag (= 100%)"
              value={rules.hoursPerDay}
              onChange={(e) => zet({ hoursPerDay: e.target.value })}
              inputProps={{ min: 1, max: 24, step: 0.5 }}
              sx={{ width: 210 }}
            />
            <TextField
              type="number" size="small" label="Inschatting overige taken (uren)"
              value={rules.fallbackHours}
              onChange={(e) => zet({ fallbackHours: e.target.value })}
              inputProps={{ min: 0, max: 24, step: 0.5 }}
              helperText="Als UrenWerk leeg is en het type hieronder niet staat"
              sx={{ width: 260 }}
            />
          </Stack>

          <Typography variant="caption" sx={{ color: T.muted, display: 'block', mb: 1 }}>
            Inschatting per taaktype (alleen gebruikt als er geen uren geregistreerd zijn):
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
            {CONFIGURABLE_TYPES.map((type) => (
              <TextField
                key={type} type="number" size="small" label={type}
                value={rules.hoursByType[type] ?? ''}
                onChange={(e) => zetType(type, e.target.value)}
                inputProps={{ min: 0, max: 24, step: 0.5 }}
                sx={{ width: 130 }}
              />
            ))}
          </Stack>

          <Typography variant="caption" sx={{ color: T.muted, display: 'block', mb: 2.5 }}>
            Een vrije dag en een draaidag staan hier niet tussen: die zijn geen inschatting
            maar een toestand van de dag. Een vrije dag toont "Vrij" en er landt niets op;
            een draaidag staat altijd op 100%.
          </Typography>

          <Typography variant="caption" sx={{ color: T.muted, display: 'block', mb: 1 }}>
            Deze statussen tellen niet mee als werkdruk:
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            {EXCLUDABLE_STATUSES.map((status) => (
              <Chip
                key={status} label={status} size="small"
                onClick={() => toggleStatus(status)}
                color={rules.excludedStatuses.includes(status) ? 'primary' : 'default'}
                variant={rules.excludedStatuses.includes(status) ? 'filled' : 'outlined'}
              />
            ))}
          </Stack>

          <FormControlLabel
            control={(
              <Checkbox
                checked={rules.skipVanallesuurtjeOnDraaidag}
                onChange={(e) => zet({ skipVanallesuurtjeOnDraaidag: e.target.checked })}
              />
            )}
            label={(
              <Typography variant="body2">
                Vanallesuurtje telt niet mee op een draaidag
              </Typography>
            )}
          />

          <Typography variant="caption" sx={{ color: T.muted, display: 'block', mt: 2, mb: 0.5 }}>
            Een taak die over meerdere dagen loopt verdeelt zijn uren:
          </Typography>
          <RadioGroup
            value={rules.verdeling}
            onChange={(e) => zet({ verdeling: e.target.value })}
          >
            {Object.entries(VERDELINGEN).map(([sleutel, tekst]) => (
              <FormControlLabel
                key={sleutel}
                value={sleutel}
                control={<Radio size="small" />}
                label={<Typography variant="body2">{tekst}</Typography>}
              />
            ))}
          </RadioGroup>
          <Typography variant="caption" sx={{ color: T.muted, display: 'block', mt: 0.5 }}>
            Voorbeeld: staat er donderdag al 6u vast en loopt er een taak van 10u over
            donderdag+vrijdag, dan is dat bij opvullen 8u/8u (twee volle dagen) en bij
            gelijk verdelen 11u/5u.
          </Typography>

          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button variant="contained" onClick={opslaan} disabled={saving}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              {saving ? 'Opslaan…' : 'Opslaan'}
            </Button>
            <Button onClick={() => { setRules(DEFAULT_CAPACITY_RULES); setMelding(null); }} disabled={saving}
              sx={{ textTransform: 'none' }}>
              Standaardwaarden invullen
            </Button>
            <Typography variant="caption" sx={{ color: T.muted }}>
              (nog niet opgeslagen)
            </Typography>
          </Stack>
          {melding && (
            <Alert severity={melding.type} sx={{ mt: 2 }}>{melding.tekst}</Alert>
          )}
        </Box>
      )}
    </Box>
  );
}
